import { useHistory } from "react-router-dom";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { PresentationChartLineIcon } from "@heroicons/react/24/solid";

import DetailsSection from "./DetailsSection";
import TitleSection from "./TitleSection";
import WebsiteSection from "./WebsiteSection";
import GoogleSection from "./GoogleSection";
import YelpSection from "./YelpSection";
import MetaSection from "./MetaSection";
import FacebookSection from "./FacebookSection";
import BingSection from "./BingSection";
import AppleSection from "./AppleSection";
import SocialNetworkSection from "./SocialNetworkSection";

import AddButton from "../../../../components/AddButton";
import EditOutlineButton from "../../../../components/EditOutlineButton";
import CloseIconButton from "../../../../components/CloseIconButton";
import { permissionsSelector } from "../../../Login/selectors/permissionSelector";
import { FIELDPERMISSIONS } from "../../../PermissionsConstants";
import { accountAtom } from "modules/Login/atoms/AccountAtom";
import { mainSelectedTab } from "../../../OfficeTab/atoms";
import { OfficeTabs } from "../../../OfficeTab/OfficeTabs";

import type { DigitalAsset, DigitalAssetPermissions } from "../types";
import { EMPTY_DIGITAL_ASSET_PERMISSIONS } from "../types";

interface DigitalAssetExpandedRowProps {
  asset: DigitalAsset;
  permissions?: DigitalAssetPermissions;
  onAdd: () => void;
  onEdit: () => void;
  onClose: () => void;
  onOpenOffice?: (officeCode: string) => void;
}

const DigitalAssetExpandedRow = ({
  asset: digitalAsset,
  permissions = EMPTY_DIGITAL_ASSET_PERMISSIONS,
  onAdd,
  onEdit,
  onClose,
  onOpenOffice,
}: DigitalAssetExpandedRowProps) => {
  const userPermissions = useRecoilValue(permissionsSelector);
  const user = useRecoilValue(accountAtom);
  const history = useHistory();
  const setSelectedIndex = useSetRecoilState(mainSelectedTab);

  const viewFacebook =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_facebook;
  const viewDirectoryGeneralDescription =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_directorygeneraldescription;
  const viewYelpProfileUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_yelpprofileurl;
  const viewYelpDescription =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_yelpdescription;
  const viewGoogleBusinessUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_googlebusinessurl;
  const viewGoogleDescription =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_googledescription;
  const viewTwitterUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_twitterurl;
  const viewLinkedInUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_linkedinurl;
  const viewYouTubeUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_youtubeurl;
  const viewInstagramUrl =
    FIELDPERMISSIONS.fieldpermission_office_digitalassets_instagramurl;

  const canOpenOffice =
    Boolean(digitalAsset.officeName) &&
    digitalAsset.officeName !== "NULL" &&
    ((user.isMexico && digitalAsset.countryCode === "MX") ||
      (!user.isMexico && digitalAsset.countryCode !== "MX") ||
      (user.isMexico && user.isFranchise && user.isNonFranchise));

  const handleOpenOffice = () => {
    if (!canOpenOffice) return;

    if (onOpenOffice) {
      onOpenOffice(digitalAsset.officeCode);
      return;
    }

    const url = "/officesettings/officesetting";
    history.push({
      pathname: url,
      state: {
        pathname: url,
        officeCode: digitalAsset.officeCode,
        action: "DigitalAsset",
      },
    });

    const index = OfficeTabs.findIndex((c) => c.to === url);
    setSelectedIndex(index);
  };
  return (
    <section className="px-8 py-4 min-h-fit">
      <div className="relative px-4 pt-2 pb-4 box-shadow border-quinary border-l-2">
        {digitalAsset && (
          <div className="">
            <div className="flex justify-between pb-1 border-secondary border-b-2">
              <div className="flex gap-2 items-center">
                <div>
                  <PresentationChartLineIcon className="text-white bg-blue-500 rounded-md h-7 w-7 p-1" />
                </div>
                <div className="flex flex-col leading tight">
                  <h3 className="text-xs font-medium text-gray-500">
                    Digital Asset
                  </h3>
                  <h1 className="mr-auto font-bold text-primary text-md uppercase">
                    {digitalAsset.name}
                  </h1>
                </div>
              </div>
              <div className="flex justify-between">
                <div className="flex items-center gap-2">
                  {permissions.canAdd && (
                    <AddButton buttonText="Add" onClick={onAdd} />
                  )}
                  {permissions.canEdit && (
                    <EditOutlineButton buttonText="Edit" onClick={onEdit} />
                  )}
                </div>

                <CloseIconButton onClick={onClose} />
              </div>
            </div>

            <div className="bg-bgDB space-y-2">
              <DetailsSection
                asset={digitalAsset}
                canOpenOffice={canOpenOffice}
                onOpenOffice={handleOpenOffice}
              />

              <TitleSection asset={digitalAsset} />

              <WebsiteSection asset={digitalAsset} />

              <GoogleSection
                asset={digitalAsset}
                canViewBusinessUrl={userPermissions.includes(
                  viewGoogleBusinessUrl,
                )}
                canViewDescription={userPermissions.includes(
                  viewGoogleDescription,
                )}
              />

              <YelpSection
                asset={digitalAsset}
                canViewProfileUrl={userPermissions.includes(viewYelpProfileUrl)}
                canViewDescription={userPermissions.includes(
                  viewYelpDescription,
                )}
              />

              <MetaSection asset={digitalAsset} />

              <FacebookSection
                asset={digitalAsset}
                canViewFacebook={userPermissions.includes(viewFacebook)}
                canViewDirectoryGeneralDescription={userPermissions.includes(
                  viewDirectoryGeneralDescription,
                )}
              />

              <BingSection asset={digitalAsset} />

              <AppleSection asset={digitalAsset} />

              <SocialNetworkSection
                asset={digitalAsset}
                canViewTwitter={userPermissions.includes(viewTwitterUrl)}
                canViewLinkedIn={userPermissions.includes(viewLinkedInUrl)}
                canViewYouTube={userPermissions.includes(viewYouTubeUrl)}
                canViewInstagram={userPermissions.includes(viewInstagramUrl)}
              />
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default DigitalAssetExpandedRow;
